import React from 'react';
import { Star, TrendingUp, BadgeCheck, Camera, MessageSquare, Search, Phone, Navigation, Globe, Bookmark, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
const includedIcons = [Camera, MessageSquare, Search];
const actionIcons = [Navigation, Bookmark, Phone, Globe];
type Profile = {
  name: string;
  category: string;
  location: string;
  rating: string;
  reviews: number;
  url: string;
  image?: string;
};
const profiles: Profile[] = [{
  name: 'Jose Junk Removal Service',
  category: 'Junk Removal',
  location: 'Florida',
  rating: '5.0',
  reviews: 48,
  url: 'https://share.google/YgRBqzQGSBTD7hIq8',
  image: 'https://cdn.magicpatterns.com/uploads/kKQ1RwEhmtKi554o7bSt5H/private_1a1e5b95-0d5f-4a6e-a505-567a124210b9.png'
}, {
  name: 'RMorales Lawn Services LLC',
  category: 'Landscaping',
  location: 'Texas',
  rating: '4.9',
  reviews: 36,
  url: 'https://share.google/S8v71YfmQV2kZIxy8',
  image: 'https://cdn.magicpatterns.com/uploads/oTAMVxe6v6TChxqDoDapyM/1111.png'
}];
export function GoogleProfilesSection() {
  const {
    t
  } = useLang();
  return <section className="py-20 bg-dark-100 border-t border-white/5" data-id="element-100" data-mp="10a3bcty033xk">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-101" data-mp="14avldpep5cuw">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-102" data-mp="14b58n58cx5ww">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/10 border border-gold/20 mb-5" data-id="element-103" data-mp="1iz9ugi2phxjw">
            <TrendingUp className="w-4 h-4 text-gold" data-id="element-104" data-mp="1o0z7u4deinb5" />
            <span className="text-gold text-sm font-bold tracking-wide uppercase" data-id="element-105" data-mp="7jl7hqpgcyj3">
              {t.google.badge}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" data-id="element-106" data-mp="chczekq3kkna">
            {t.google.title}{' '}
            <span className="text-gold" data-id="element-107" data-mp="299seinwpyr24">{t.google.titleHighlight}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-4" data-id="element-108" data-mp="29bvcpv80zqhx">
            {t.google.descA}{' '}
            <strong className="text-white" data-id="element-109" data-mp="24gzdob4dg2vb">{t.google.descIndependent}</strong>{' '}
            {t.google.descB}{' '}
            <strong className="text-gold" data-id="element-110" data-mp="1vidwokejsvwn">{t.google.descPrice}</strong>
            {t.google.descC}
          </p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto mb-8" data-id="element-111" data-mp="1qirio8qwbbms">
            <strong className="text-gray-300" data-id="element-112" data-mp="1qlnhdzaki4ca">{t.google.noteLabel}</strong>{' '}
            {t.google.noteText}
          </p>

          {/* What's included */}
          <div className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto" data-id="element-113" data-mp="1hjxosq4y8zcx">
            {t.google.included.map((text, i) => {
            const Icon = includedIcons[i];
            return <div key={i} className="inline-flex items-center gap-2 bg-dark border border-white/10 rounded-full px-4 py-2 text-sm text-gray-300" data-id="element-114" data-mp="3bxykj257d68s" data-mpi={i}>
                  <Icon className="w-4 h-4 text-gold" data-id="element-115" data-mp="12mee7layi2rc" />
                  {text}
                </div>;
          })}
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start" data-id="element-116" data-mp="1bgj0rsj24s12">
          {profiles.map((profile, index) => <motion.a key={index} href={profile.url} target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.4
        }} className="group block bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow" data-id="element-117" data-mp="erbmn5krpl76" data-mpi={index}>
              {/* Photo header (like a Google profile) */}
              <div className="relative aspect-[16/9] overflow-hidden bg-gray-100" data-id="element-118" data-mp="1xqj16rlbzls6">
                {profile.image ? <img src={profile.image} alt={`${t.google.profileAltPrefix} ${profile.name}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-119" data-mp="2bm4qz0ze29ln" /> : <div className="w-full h-full flex items-center justify-center bg-gray-200" data-id="element-120" data-mp="2glpy3yoppo9g">
                    <MapPin className="w-10 h-10 text-gray-400" data-id="element-121" data-mp="2ve9n7vf11v7w" />
                  </div>}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-xs font-bold text-green-700 bg-white/95 px-2.5 py-1 rounded-full shadow-sm" data-id="element-122" data-mp="2qfq1nw4cilg2">
                  <BadgeCheck className="w-3.5 h-3.5 text-green-600" data-id="element-123" data-mp="2zfmr3zyk7oc6" />
                  {t.google.optimized}
                </div>
              </div>

              {/* Google-style info panel */}
              <div className="p-5" data-id="element-124" data-mp="3nyeupd784a85">
                <h3 className="text-gray-900 font-medium text-xl leading-snug mb-1" data-id="element-125" data-mp="39z5n31x32ofs">
                  {profile.name}
                </h3>

                <div className="flex items-center gap-1.5 mb-1" data-id="element-126" data-mp="3dzpm6b17w24k">
                  <span className="text-[#70757a] font-medium text-sm" data-id="element-127" data-mp="309m5s0p0r2n1">
                    {profile.rating}
                  </span>
                  <div className="flex gap-0.5" data-id="element-128" data-mp="35143mfvqwn3s">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#fbbc04] text-[#fbbc04]" data-id="element-129" data-mp="2qchfpps1yn0y" data-mpi={i} />)}
                  </div>
                  <span className="text-[#70757a] text-sm" data-id="element-130" data-mp="2hbt411q4syr8">
                    ({profile.reviews})
                  </span>
                </div>

                <p className="text-[#70757a] text-sm mb-4" data-id="element-131" data-mp="rtcmzb7rwpz0">
                  {profile.category} · {profile.location}
                </p>

                {/* Google action buttons */}
                <div className="flex items-center justify-between border-t border-gray-100 pt-4" data-id="element-132" data-mp="n0t9sfczzwuq">
                  {t.google.actions.map((label, i) => {
                const Icon = actionIcons[i];
                return <div key={i} className="flex flex-col items-center gap-1.5 flex-1" data-id="element-133" data-mp="1asgtodbue7ni" data-mpi={i}>
                        <div className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-[#1a73e8]" data-id="element-134" data-mp="15razsc6g4ece">
                          <Icon className="w-4 h-4" data-id="element-135" data-mp="wt8qzagh0hek" />
                        </div>
                        <span className="text-[11px] text-[#1a73e8] font-medium" data-id="element-136" data-mp="3mhhuhvn5jt3b">
                          {label}
                        </span>
                      </div>;
              })}
                </div>
              </div>
            </motion.a>)}
        </div>
      </div>
    </section>;
}